export const COPILOT_SKILL_SECURITY_DOMAINS = [
  'web-app',
  'external-network',
  'internal-network',
  'booth',
] as const;

export type CoPilotSkillSecurityDomain =
  (typeof COPILOT_SKILL_SECURITY_DOMAINS)[number];

export const COPILOT_SKILL_CLIENT_SURFACES = [
  'web-platform',
  'torch',
  'terra-mcp',
] as const;

export type CoPilotSkillClientSurface =
  (typeof COPILOT_SKILL_CLIENT_SURFACES)[number];

export const PROXY_GATED_COPILOT_SKILL_KEYS = [
  'copilot/endpoint-creation',
  'copilot/roles-editing',
  'copilot/credential-auth-material',
  'operator/portal-proxy-testing',
] as const;

export type CoPilotSkillAvailability = {
  securityDomain: CoPilotSkillSecurityDomain;
  sessionDomains: CoPilotSkillSecurityDomain[];
  clientSurfaces: CoPilotSkillClientSurface[];
  requiresProxyTools: boolean;
  requiresTerraMcpTools: boolean;
};

export type CoPilotSkillAvailabilityInput = {
  securityDomain: CoPilotSkillSecurityDomain;
  sessionDomains: readonly CoPilotSkillSecurityDomain[];
  clientSurfacesOverride?: readonly CoPilotSkillClientSurface[];
  bundleId: string;
  skillDir: string;
  mentionedToolIds: readonly string[];
};

const PROXY_GATED_SKILL_KEY_SET = new Set<string>(
  PROXY_GATED_COPILOT_SKILL_KEYS,
);

export function isProxyGatedCoPilotSkillKey(skillKey: string): boolean {
  return PROXY_GATED_SKILL_KEY_SET.has(skillKey);
}

export function skillReferencesTerraMcpTools({
  mentionedToolIds,
  description,
  bodyMarkdown,
}: {
  mentionedToolIds: readonly string[];
  description: string;
  bodyMarkdown: string;
}): boolean {
  if (mentionedToolIds.some((toolId) => toolId.startsWith('mcp__terra__'))) {
    return true;
  }

  const combined = `${description}\n${bodyMarkdown}`.toLowerCase();
  return (
    combined.includes('mcp__terra__') ||
    combined.includes('terra mcp') ||
    combined.includes('terra_query')
  );
}

export function resolveCoPilotSkillClientSurfaces({
  requiresProxyTools,
  clientSurfacesOverride,
}: {
  requiresProxyTools: boolean;
  clientSurfacesOverride?: readonly CoPilotSkillClientSurface[];
}): CoPilotSkillClientSurface[] {
  if (clientSurfacesOverride?.length) {
    return [...clientSurfacesOverride];
  }

  if (requiresProxyTools) {
    return ['torch'];
  }

  return ['web-platform', 'torch'];
}

export function buildCoPilotSkillAvailabilityFromSkill({
  securityDomain,
  sessionDomains,
  clientSurfacesOverride,
  bundleId,
  skillDir,
  description,
  bodyMarkdown,
  mentionedToolIds,
}: CoPilotSkillAvailabilityInput & {
  description: string;
  bodyMarkdown: string;
}): CoPilotSkillAvailability {
  const skillKey = `${bundleId}/${skillDir}`;
  const requiresProxyTools = isProxyGatedCoPilotSkillKey(skillKey);
  const requiresTerraMcpTools = skillReferencesTerraMcpTools({
    mentionedToolIds,
    description,
    bodyMarkdown,
  });

  return {
    securityDomain,
    sessionDomains: [...sessionDomains],
    clientSurfaces: resolveCoPilotSkillClientSurfaces({
      requiresProxyTools,
      clientSurfacesOverride,
    }),
    requiresProxyTools,
    requiresTerraMcpTools,
  };
}

export function coPilotSkillMatchesSecurityDomainFilter({
  availability,
  filter,
}: {
  availability: CoPilotSkillAvailability | undefined;
  filter: CoPilotSkillSecurityDomain | '__all__';
}): boolean {
  if (filter === '__all__') {
    return true;
  }
  return (availability?.sessionDomains ?? []).includes(filter);
}

export function coPilotSkillMatchesClientSurfaceFilter({
  availability,
  filter,
}: {
  availability: CoPilotSkillAvailability | undefined;
  filter: CoPilotSkillClientSurface | '__all__';
}): boolean {
  if (filter === '__all__') {
    return true;
  }
  return (availability?.clientSurfaces ?? []).includes(filter);
}
